import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { selfServiceClient } from '../services/selfService';
import {
  DebitMemoBillingTableItem,
  InvoiceBillingTableItem,
  InvoiceRaw,
  MemoStatus,
  ZuoraDebitMemo,
} from './types';
import { useMetricsQuery } from './useMetricsQuery';

export const invoiceBillingItemsQueryKey = 'getInvoiceBillingItems';
export const debitMemoBillingItemsQueryKey = 'getDebitMemoBillingItems';

const getInvoiceBillingItems = () =>
  selfServiceClient.getInvoices().then(({ invoices }) => invoices.map(adaptInvoice));

const getDebitMemoBillingItems = () =>
  selfServiceClient.getDebitMemos().then(({ debitMemos }) => debitMemos.map(adaptDebitMemo));

const adaptDebitMemo = (debitMemo: ZuoraDebitMemo): DebitMemoBillingTableItem => ({
  Id: debitMemo.id,
  Amount: debitMemo.amount,
  Balance: debitMemo.balance,
  DueDate: debitMemo.dueDate,
  InvoiceDate: debitMemo.createdDate,
  InvoiceNumber: debitMemo.number,
  AmountWithoutTax: debitMemo.totalTaxExemptAmount,
  TaxAmount: debitMemo.taxAmount,
  PONumber__c: debitMemo.PONumber__c,
  status: debitMemo.status,
  itemType: 'debitMemo',
});

const adaptInvoice = (invoice: InvoiceRaw): InvoiceBillingTableItem => ({
  ...invoice,
  itemType: 'invoice',
});

type BillingQueriesResponse = [[InvoiceBillingTableItem[]], [DebitMemoBillingTableItem[]]];

const isPosted = <T extends { status?: MemoStatus }>(item: T) => item.status === 'Posted';

const debitMemoSelector = (items: DebitMemoBillingTableItem[]) => items.filter(isPosted);

export const useBillingQueries = () => {
  const metricQuery = useMetricsQuery();
  const billingQueries = useQueries<BillingQueriesResponse>({
    queries: [
      {
        queryKey: [invoiceBillingItemsQueryKey],
        queryFn: getInvoiceBillingItems,
        retry: false,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: [debitMemoBillingItemsQueryKey],
        queryFn: getDebitMemoBillingItems,
        retry: false,
        refetchOnWindowFocus: false,
        select: debitMemoSelector,
      },
    ],
  });
  const [invoiceQuery, dmQuery] = billingQueries;
  const queries = [metricQuery, ...billingQueries];

  const refetch = () => Promise.all(queries.map(({ refetch }) => refetch()));

  const isSuccess = queries.every(({ isSuccess }) => isSuccess);
  const isLoading = queries.some(({ isLoading }) => isLoading);
  const isError = queries.some(({ isError }) => isError);

  return {
    debitMemos: dmQuery.data,
    invoices: invoiceQuery.data,
    isSuccess,
    isLoading,
    isError,
    refetch,
  };
};

export const useInvoiceTableItems = () => {
  const { debitMemos = [], invoices = [], ...rest } = useBillingQueries();
  const items = useMemo(() => {
    return [...invoices, ...debitMemos];
  }, [debitMemos, invoices]);

  return { items, ...rest };
};
